import type { novaToolDefs } from '@newfront-insurance/ai-api';
import { ChainOfThoughtSearchResult, ChainOfThoughtSearchResults, SourceBadge } from '@newfront-insurance/nova-core';
import type { InferUITool } from 'ai';

import type { CoTStep } from '../lib/chain-of-thought-utils';
import { type CoTSearchResult, MAX_OUTPUT_LENGTH } from '../lib/chain-of-thought-utils';
import type { SourcesMap, SourceUrlPart } from '../lib/sources';

type ToolIO<T extends keyof typeof novaToolDefs> = InferUITool<(typeof novaToolDefs)[T]>;
type DataExplorerOutputWithSources = ToolIO<'dataExplorer'>['output'] & {
  sources?: Array<{
    sourceId: string;
    url: string;
  }>;
};

export type CoTToolInputProps = {
  [K in keyof typeof novaToolDefs]: { toolName: K; input: ToolIO<K>['input'] };
}[keyof typeof novaToolDefs] & { priorSteps?: CoTStep[] };

/** Look up a filename from prior documentSearch outputs by fileId */
function findFilenameFromPriorSteps(
  fileId: string,
  priorSteps: CoTStep[],
): { filename: string; link?: string } | undefined {
  for (const step of priorSteps) {
    if (step.type !== 'tool' || step.toolName !== 'documentSearch' || !step.output) continue;
    const { results } = step.output as ToolIO<'documentSearch'>['output'];
    if (!Array.isArray(results)) continue;
    const match = results.find((r) => r.fileId === fileId);
    if (match) return { filename: match.filename, link: match.navigatorUrl };
  }
  return undefined;
}

/** Renders tool input as a description for a CoT step — returns null when no custom renderer exists */
export function CoTToolInput({ toolName, input, priorSteps }: CoTToolInputProps) {
  switch (toolName) {
    case 'documentSearch': {
      const { query } = input;
      return query ? <span>Searching for &ldquo;{query}&rdquo;</span> : null;
    }
    case 'readFile': {
      const { fileId, pages } = input;
      const file = priorSteps ? findFilenameFromPriorSteps(fileId, priorSteps) : undefined;
      const pagesLabel = pages ? ` (pages ${pages})` : '';
      if (file) {
        return (
          <ChainOfThoughtSearchResults>
            {file.link ? (
              <a href={file.link} target="_blank" rel="noopener noreferrer">
                <ChainOfThoughtSearchResult>
                  {file.filename}
                  {pagesLabel}
                </ChainOfThoughtSearchResult>
              </a>
            ) : (
              <ChainOfThoughtSearchResult>
                {file.filename}
                {pagesLabel}
              </ChainOfThoughtSearchResult>
            )}
          </ChainOfThoughtSearchResults>
        );
      }
      return <span>Reading file{pagesLabel}</span>;
    }
    case 'processFiles': {
      const { context } = input;
      return context ? <span>{context}</span> : null;
    }
    case 'dataExplorer': {
      const { lastMessageReasoning } = input;
      return lastMessageReasoning ? <span>{lastMessageReasoning}</span> : null;
    }
    default:
      return null;
  }
}

/** Renders search result badges from tool output — returns null when no extractor exists or no results */
export function CoTToolSearchResults({
  toolName,
  output,
  sourcesMap,
}: {
  toolName: string;
  output: unknown;
  sourcesMap?: SourcesMap;
}) {
  // Data explorer: render source badges + remaining count badge
  if (toolName === 'dataExplorer') {
    return <DataExplorerCoTSearchResults output={output} sourcesMap={sourcesMap} />;
  }

  const results = extractSearchResults(toolName, output);
  if (results.length === 0) return null;

  return (
    <ChainOfThoughtSearchResults>
      {results.map((result, index) =>
        result.link ? (
          <a key={result.link} href={result.link} target="_blank" rel="noopener noreferrer">
            <ChainOfThoughtSearchResult>{result.title}</ChainOfThoughtSearchResult>
          </a>
        ) : (
          // eslint-disable-next-line react/no-array-index-key
          <ChainOfThoughtSearchResult key={index}>{result.title}</ChainOfThoughtSearchResult>
        ),
      )}
    </ChainOfThoughtSearchResults>
  );
}

function DataExplorerCoTSearchResults({ output, sourcesMap }: { output: unknown; sourcesMap?: SourcesMap }) {
  const { data, sources } = output as DataExplorerOutputWithSources;

  if (!sources?.length && (!data || data.rowCount === 0)) return null;

  const sourceIndexMap = new Map(Array.from(sourcesMap?.keys() ?? []).map((id, i) => [id, i]));

  const matchedSources = (sources ?? [])
    .map((s: NonNullable<DataExplorerOutputWithSources['sources']>[number]) => {
      const source = sourcesMap?.get(s.sourceId);
      if (!source) return null;
      const globalIndex = sourceIndexMap.get(s.sourceId) ?? 0;
      return { source, index: globalIndex };
    })
    .filter((s): s is { source: SourceUrlPart; index: number } => s !== null);

  const totalResults = data?.rowCount ?? 0;
  const remainingCount = totalResults - matchedSources.length;

  if (matchedSources.length === 0 && totalResults > 0) {
    return (
      <ChainOfThoughtSearchResults>
        <ChainOfThoughtSearchResult>
          {totalResults} result{totalResults === 1 ? '' : 's'} found
        </ChainOfThoughtSearchResult>
      </ChainOfThoughtSearchResults>
    );
  }

  if (matchedSources.length === 0) return null;

  return (
    <ChainOfThoughtSearchResults>
      {matchedSources.map(({ source, index }) => (
        <SourceBadge key={source.sourceId} source={source} index={index} size="sm" />
      ))}
      {remainingCount > 0 && (
        <ChainOfThoughtSearchResult>
          {remainingCount} other result{remainingCount === 1 ? '' : 's'}
        </ChainOfThoughtSearchResult>
      )}
    </ChainOfThoughtSearchResults>
  );
}

/** Renders tool output in a CoT step — uses custom renderer if available, falls back to truncated string */
export function CoTToolOutput({ toolName, output }: { toolName: string; output: unknown }) {
  switch (toolName) {
    case 'documentSearch': {
      const { searchSummary } = output as ToolIO<'documentSearch'>['output'];
      return searchSummary ? <p>{searchSummary}</p> : null;
    }
    case 'readFile':
      return null;
    case 'dataExplorer': {
      const { reasoning } = output as ToolIO<'dataExplorer'>['output'];
      return reasoning ? <p>{reasoning}</p> : null;
    }
    default: {
      if (typeof output === 'string') {
        const text = output.length > MAX_OUTPUT_LENGTH ? `${output.slice(0, MAX_OUTPUT_LENGTH)}…` : output;
        return <p>{text}</p>;
      }
      return null;
    }
  }
}

function extractSearchResults(toolName: string, output: unknown): CoTSearchResult[] {
  switch (toolName) {
    case 'documentSearch': {
      const { results } = output as ToolIO<'documentSearch'>['output'];
      if (!Array.isArray(results)) return [];
      return results
        .filter((r) => typeof r.filename === 'string')
        .map((r) => ({ title: r.filename, link: r.navigatorUrl }));
    }
    default:
      return [];
  }
}
